import { useEffect, useRef, useState, useCallback } from "react";

export const useSSE = (url, onMessage, options = {}) => {
    const {
        retryInterval = 5000,
        forceReconnectInterval = 10 * 60 * 1000,
        getFreshUrl,
        debug = false,
    } = options;

    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const forceReconnectTimerRef = useRef(null);
    const onMessageRef = useRef(onMessage);

    const [status, setStatus] = useState("disconnected");

    const log = (...args) => {
        if (debug) console.log(...args);
    };

    // ✅ Always keep latest callback without triggering reconnect
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    const cleanup = useCallback(() => {
        if (eventSourceRef.current) {
            log("🧹 Cleaning up SSE connection");
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (forceReconnectTimerRef.current) {
            clearInterval(forceReconnectTimerRef.current);
            forceReconnectTimerRef.current = null;
        }
    }, [debug]);

    const connect = useCallback(() => {
        if (!url && !getFreshUrl) return;

        // ✅ Prevent duplicate connections
        if (
            eventSourceRef.current &&
            eventSourceRef.current.readyState !== EventSource.CLOSED
        ) {
            log("⚠️ SSE already connected, skipping...");
            return;
        }

        const finalUrl = getFreshUrl ? getFreshUrl() : url;

        if (!finalUrl) return;

        console.log("🔌 Connecting SSE:", finalUrl);

        const source = new EventSource(finalUrl);
        eventSourceRef.current = source;

        source.onopen = () => {
            console.log("✅ SSE connected");
            setStatus("connected");
        };

        source.onmessage = (event) => {
            if (!event.data) return;

            try {
                const data = JSON.parse(event.data);

                if (onMessageRef.current) {
                    onMessageRef.current(data);
                }

            } catch (err) {
                console.error("❌ SSE parsing error:", err);
            }
        };

        source.onerror = (err) => {
            console.error("❌ SSE error:", err);
            setStatus("disconnected");

            source.close();
            eventSourceRef.current = null;

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }

            reconnectTimeoutRef.current = setTimeout(() => {
                console.log("🔄 Reconnecting SSE...");
                connect();
            }, retryInterval);
        };
    }, [url, retryInterval, getFreshUrl, debug]);

    const manualReconnect = useCallback(() => {
        console.log("🔁 Manual reconnect triggered");
        cleanup();
        connect();
    }, [cleanup, connect]);

    // ✅ Main effect (ONLY depends on URL)
    useEffect(() => {
        if (!url && !getFreshUrl) return;

        connect();

        // ✅ Force reconnect (long-lived connection safety)
        if (forceReconnectInterval) {
            forceReconnectTimerRef.current = setInterval(() => {
                log("🔄 Force reconnect triggered");
                manualReconnect();
            }, forceReconnectInterval);
        }

        return () => {
            cleanup();
        };
    }, [url]); // 🔥 IMPORTANT: only URL dependency

    return {
        status, // "connected" | "disconnected"
        reconnect: manualReconnect,
    };
};